import { formatValue } from '@/lib/formatters';

export type ShipmentPipelineStatus =
  | 'ACCEPTED'
  | 'ARRIVED_AT_CN_DROP_OFF'
  | 'PROCESSED_AT_CN_DROP_OFF'
  | 'IN_TRANSIT_TO_CN_SORT_CENTER'
  | 'ARRIVED_AT_CN_SORT_CENTER'
  | 'MARKING'
  | 'READY_FOR_RU_DISPATCH'
  | 'DEPARTED_CN_SORT_CENTER'
  | 'EXPORT_CLEARED_IN_CN'
  | 'ARRIVED_AT_RU_CUSTOMS_BORDER'
  | 'DELIVERED_TO_CUSTOMS_TERMINAL'
  | 'IMPORT_CLEARED_IN_RU'
  | 'DELIVERY_TO_SELECTED_WAREHOUSE'
  | 'COMPLETED'
  | 'CANCELLED';

export type ShipmentPipelineStageKey = 'customs' | 'delivery' | 'marking' | 'preparation' | 'reception' | 'transit';

export const shipmentPipelineStatusLabels: Record<ShipmentPipelineStatus, string> = {
  ACCEPTED: 'Оформлен',
  ARRIVED_AT_CN_DROP_OFF: 'Прибыл на drop-off Китай',
  ARRIVED_AT_CN_SORT_CENTER: 'Прибыл на сортцентр Китай',
  ARRIVED_AT_RU_CUSTOMS_BORDER: 'Прибыл на таможенную границу РФ',
  CANCELLED: 'Отменена',
  COMPLETED: 'Доставлен',
  DELIVERED_TO_CUSTOMS_TERMINAL: 'Груз сдан на таможенный терминал',
  DELIVERY_TO_SELECTED_WAREHOUSE: 'Доставка до выбранного склада',
  DEPARTED_CN_SORT_CENTER: 'Покинул сортцентр Китай',
  EXPORT_CLEARED_IN_CN: 'Оформлен экспорт в Китае',
  IMPORT_CLEARED_IN_RU: 'Оформлен импорт в РФ',
  IN_TRANSIT_TO_CN_SORT_CENTER: 'В пути на сортцентр Китай',
  MARKING: 'Маркировка',
  PROCESSED_AT_CN_DROP_OFF: 'Обработан на drop-off Китай',
  READY_FOR_RU_DISPATCH: 'Готов к отправке в РФ'
};

export const inTransitShipmentPipelineStatuses = [
  'ACCEPTED',
  'ARRIVED_AT_CN_DROP_OFF',
  'PROCESSED_AT_CN_DROP_OFF',
  'IN_TRANSIT_TO_CN_SORT_CENTER',
  'ARRIVED_AT_CN_SORT_CENTER',
  'MARKING',
  'READY_FOR_RU_DISPATCH',
  'DEPARTED_CN_SORT_CENTER',
  'EXPORT_CLEARED_IN_CN',
  'ARRIVED_AT_RU_CUSTOMS_BORDER',
  'DELIVERED_TO_CUSTOMS_TERMINAL'
] satisfies ShipmentPipelineStatus[];

export const customsShipmentPipelineStatuses = [
  'IMPORT_CLEARED_IN_RU'
] satisfies ShipmentPipelineStatus[];

export const warehouseShipmentPipelineStatuses = [
  'DELIVERY_TO_SELECTED_WAREHOUSE'
] satisfies ShipmentPipelineStatus[];

export const completedShipmentPipelineStatuses = [
  'COMPLETED',
  'CANCELLED'
] satisfies ShipmentPipelineStatus[];

export const activeShipmentPipelineStatuses = [
  'ACCEPTED',
  'ARRIVED_AT_CN_DROP_OFF',
  'PROCESSED_AT_CN_DROP_OFF',
  'IN_TRANSIT_TO_CN_SORT_CENTER',
  'ARRIVED_AT_CN_SORT_CENTER',
  'MARKING',
  'READY_FOR_RU_DISPATCH',
  'DEPARTED_CN_SORT_CENTER',
  'EXPORT_CLEARED_IN_CN',
  'ARRIVED_AT_RU_CUSTOMS_BORDER',
  'DELIVERED_TO_CUSTOMS_TERMINAL',
  'IMPORT_CLEARED_IN_RU',
  'DELIVERY_TO_SELECTED_WAREHOUSE'
] satisfies ShipmentPipelineStatus[];

export const acceptedShipmentPipelineStatuses = [
  'ACCEPTED'
] satisfies ShipmentPipelineStatus[];

export const beforeDropOffStatuses = [
  'ACCEPTED',
  'ARRIVED_AT_CN_DROP_OFF'
] satisfies ShipmentPipelineStatus[];

export const receptionProcessingShipmentPipelineStatuses = [
  'ARRIVED_AT_CN_DROP_OFF',
  'PROCESSED_AT_CN_DROP_OFF',
  'IN_TRANSIT_TO_CN_SORT_CENTER',
  'ARRIVED_AT_CN_SORT_CENTER',
  'READY_FOR_RU_DISPATCH'
] satisfies ShipmentPipelineStatus[];

export const markingShipmentPipelineStatuses = [
  'MARKING'
] satisfies ShipmentPipelineStatus[];

export const internationalLogisticsShipmentPipelineStatuses = [
  'DEPARTED_CN_SORT_CENTER',
  'EXPORT_CLEARED_IN_CN',
  'ARRIVED_AT_RU_CUSTOMS_BORDER',
  'DELIVERED_TO_CUSTOMS_TERMINAL'
] satisfies ShipmentPipelineStatus[];

export const customsProcessingShipmentPipelineStatuses = [
  'IMPORT_CLEARED_IN_RU'
] satisfies ShipmentPipelineStatus[];

export const domesticLogisticsShipmentPipelineStatuses = [
  'DELIVERY_TO_SELECTED_WAREHOUSE',
  'COMPLETED',
  'CANCELLED'
] satisfies ShipmentPipelineStatus[];

const isShipmentPipelineStatus = (status: string): status is ShipmentPipelineStatus => {
  return status in shipmentPipelineStatusLabels;
};

const includesShipmentPipelineStatus = (
  statuses: readonly ShipmentPipelineStatus[],
  status: ShipmentPipelineStatus
): boolean => {
  return statuses.includes(status);
};

export const getShipmentPipelineStageKey = (status: string | null): ShipmentPipelineStageKey => {
  if (!status || !isShipmentPipelineStatus(status)) return 'preparation';

  if (includesShipmentPipelineStatus(acceptedShipmentPipelineStatuses, status)) return 'preparation';
  if (includesShipmentPipelineStatus(receptionProcessingShipmentPipelineStatuses, status)) return 'reception';
  if (includesShipmentPipelineStatus(markingShipmentPipelineStatuses, status)) return 'marking';
  if (includesShipmentPipelineStatus(internationalLogisticsShipmentPipelineStatuses, status)) return 'transit';
  if (includesShipmentPipelineStatus(customsProcessingShipmentPipelineStatuses, status)) return 'customs';
  if (includesShipmentPipelineStatus(domesticLogisticsShipmentPipelineStatuses, status)) return 'delivery';

  return 'preparation';
};

export const getNextShipmentPipelineStatusLabel = (status: string | null): string | null => {
  if (!status || !isShipmentPipelineStatus(status)) return null;

  const statusIndex = activeShipmentPipelineStatuses.indexOf(status);

  if (statusIndex === -1) return null;

  const nextStatus = activeShipmentPipelineStatuses[statusIndex + 1];

  return nextStatus ? shipmentPipelineStatusLabels[nextStatus] : null;
};

export const getShipmentPipelineStatusLabel = (status: string | null): string => {
  if (status && isShipmentPipelineStatus(status)) {
    return shipmentPipelineStatusLabels[status];
  }

  return formatValue({ value: status });
};